import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CREDIT_CATEGORIES, CreditCategory } from '@troia-v3/types';
import type { ConsumptionByDayItem } from '@troia-v3/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';

const COLORS = ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#94a3b8'];

interface ConsumptionChartProps {
  data: ConsumptionByDayItem[];
  totals: Array<{ category: CreditCategory; total: number }>;
  height?: number;
}

export function ConsumptionChart({ data, totals, height = 300 }: ConsumptionChartProps) {
  // Top 5 categories by total, rest grouped as "Outros"
  const topCategories = useMemo(() => {
    const sorted = [...totals].sort((a, b) => b.total - a.total);
    return sorted.slice(0, 5).map((t) => t.category);
  }, [totals]);

  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const chartData = useMemo(() => {
    return data.map((item) => {
      const row: Record<string, string | number> = { date: item.date.slice(5).replace('-', '/') };
      let othersTotal = 0;
      for (const [cat, amount] of Object.entries(item.categories)) {
        if (topCategories.includes(cat as CreditCategory)) {
          row[cat] = amount;
        } else {
          othersTotal += amount;
        }
      }
      if (othersTotal > 0) row['others'] = othersTotal;
      return row;
    });
  }, [data, topCategories]);

  const seriesKeys = useMemo(() => {
    const keys: string[] = [...topCategories];
    if (data.some((d) => {
      const topSum = topCategories.reduce((s, c) => s + (d.categories[c] || 0), 0);
      return d.total > topSum;
    })) {
      keys.push('others');
    }
    return keys;
  }, [topCategories, data]);

  const getLabel = (key: string) =>
    key === 'others' ? 'Outros' : CREDIT_CATEGORIES[key as CreditCategory]?.label || key;

  const toggleSeries = (key: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const numberFormat = new Intl.NumberFormat('pt-BR');

  return (
    <Card className="rounded-xl">
      <CardHeader className="pb-1.5 py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
            Consumo Diário
          </span>
          <div className="flex flex-wrap gap-2">
            {seriesKeys.map((key, i) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleSeries(key)}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors"
                style={{
                  backgroundColor: hiddenSeries.has(key) ? 'transparent' : `${COLORS[i]}15`,
                  color: hiddenSeries.has(key) ? '#94a3b8' : COLORS[i],
                  border: `1px solid ${hiddenSeries.has(key) ? '#e2e8f0' : COLORS[i]}30`,
                }}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: hiddenSeries.has(key) ? '#cbd5e1' : COLORS[i] }}
                />
                {getLabel(key)}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 pb-4">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--theme-card-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}
              formatter={(value, name) => [numberFormat.format(Number(value) || 0), getLabel(String(name))]}
              labelFormatter={(label) => `Dia ${String(label)}`}
            />
            {seriesKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="consumption"
                fill={COLORS[i]}
                hide={hiddenSeries.has(key)}
                radius={i === seriesKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
